import React from 'react'
import { createGlobalStyle } from 'styled-components'
import { Button } from 'semantic-ui-react'
import { formatMoney, formatShortDate } from 'utility/crm/dates'
import { quoteLines } from 'utility/crm/proposal'
import { useCrmLookups } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'

// Printing shows only the proposal: everything else on the page is hidden.
const PrintStyle = createGlobalStyle`
    @media print {
        body > *:not([data-crm-print="true"]) { display: none !important; }
        [data-crm-print="true"] { position: static !important; background: #fff !important; display: block !important; }
        [data-crm-print="true"] [role="dialog"] { box-shadow: none !important; width: 100% !important; max-height: none !important; height: auto !important; color: #000; }
        .crm-no-print { display: none !important; }
    }
`

/**
 * Printable proposal. Built as a normal React view (text nodes only, never HTML strings) and printed with
 * the browser's print dialog.
 * @param {{ deal: import('utility/crm/types').CrmDeal|null, proposal: import('utility/crm/types').CrmProposal|null, onClose: () => void }} props
 */
export const ProposalPrint = ({ deal, proposal, onClose }) => {
    const { lookups } = useCrmLookups()
    const open = Boolean(deal && proposal)
    const plan = proposal ? (lookups?.Plans || []).find(p => p.Id === proposal.Plan) : undefined
    const lines = proposal ? quoteLines(proposal, plan, lookups?.ExtraUserPrice || 0) : []

    return (
        <>
            {/* Only while the proposal is open: otherwise Ctrl+P on the pipeline would print a blank page. */}
            {open && <PrintStyle />}
            <CrmModal
                open={open}
                onClose={onClose}
                title={deal ? `Proposal for ${deal.AccountName}` : 'Proposal'}
                width={760}
                printable
                footer={
                    <>
                        <Button type="button" onClick={onClose}>
                            Close
                        </Button>
                        <Button type="button" primary icon="print" content="Print / save as PDF" onClick={() => window.print()} />
                    </>
                }
            >
                {deal && proposal ? (
                    <div>
                        <h2 style={{ marginTop: 0 }}>eAuditor proposal</h2>
                        <p>
                            <strong>Prepared for:</strong> {deal.AccountName}
                            {deal.ContactName ? ` · ${deal.ContactName}` : ''}
                            <br />
                            <strong>Date:</strong> {formatShortDate(proposal.SavedOn || new Date())}
                            <br />
                            <strong>Plan:</strong> {proposal.PlanName} · {proposal.Sites} site{proposal.Sites === 1 ? '' : 's'} · {proposal.Users} users · billed {proposal.Billing}
                        </p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                {lines.map(line => (
                                    <tr key={line.label} style={{ borderBottom: '1px solid rgba(128,128,128,.35)' }}>
                                        <td style={{ padding: '8px 0', fontWeight: line.strong ? 700 : 400 }}>{line.label}</td>
                                        <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: line.strong ? 700 : 400 }}>
                                            {line.amount < 0 ? `−${formatMoney(-line.amount)}` : formatMoney(line.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p style={{ marginTop: 16, opacity: 0.75, fontSize: '0.85rem' }}>{proposal.Summary}</p>
                    </div>
                ) : null}
            </CrmModal>
        </>
    )
}
